import './Industries.css';
import React, { useState, useEffect, useRef } from "react";
import useAnimationOnScroll from '../hooks/useAnimationOnScroll';
import {
    DollarSign,
    HeartPulse,
    ShoppingBag,
    Building,
    GraduationCap,
    BarChart3
} from 'lucide-react';

const Industries = () => {
    const headerRef = useAnimationOnScroll();
    const containerRef = useRef(null);
    const [clickedIndustry, setClickedIndustry] = useState(null);
    const [hoveredIndustry, setHoveredIndustry] = useState(null);

    // Handle click outside to close expanded content
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (clickedIndustry && containerRef.current && !containerRef.current.contains(event.target)) {
                setClickedIndustry(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [clickedIndustry]);

    const industries = [
        {
            id: 'fintech',
            name: 'FinTech',
            icon: DollarSign,
            description: 'Payment gateway, AML, risk management, multi-channel integration',
            position: 'top'
        },
        {
            id: 'healthtech',
            name: 'HealthTech',
            icon: HeartPulse,
            description: 'EHR/EMR, RCM, patient portals, ABDM, FHIR, HIPAA',
            position: 'top-right'
        },
        {
            id: 'ecommerce',
            name: 'eCommerce',
            icon: ShoppingBag,
            description: 'Robust, scalable platforms with payment integrations and advanced analytics',
            position: 'bottom-right'
        },
        {
            id: 'building',
            name: 'Building Automation',
            icon: Building,
            description: 'Fire systems, CCTV, BMS, access control',
            position: 'bottom'
        },
        {
            id: 'edtech',
            name: 'EdTech',
            icon: GraduationCap,
            description: 'Virtual classrooms, student information systems, secure digital learning infrastructure',
            position: 'bottom-left'
        },
        {
            id: 'martech',
            name: 'MarTech',
            icon: BarChart3,
            description: 'Lead generation, delivery, qualification, governance',
            position: 'top-left'
        }
    ];

    return (
        <section className="section industries-section">
            <div className="industries-container" ref={containerRef}>
                {/* Section Header */}
                <div ref={headerRef} className="industries-section-header animate-in">
                    <span className="industries-section-subtitle">INDUSTRIES WE SERVE</span>
                    <h2 className="industries-section-title">Digital Transformation Across Industries</h2>
                    <p className="industries-section-description">
                        We bring deep domain expertise and proven success to accelerate technology transformation across diverse industry verticals, creating interconnected solutions that drive innovation and growth.
                    </p>
                </div>

                {/* Star Network Visualization */}
                <div className="industries-star-network-container">
                    {/* Left Side Panel - Industry Names */}
                    <div className="industries-side-panel industries-left-panel">
                        <div className="industries-panel-list">
                            <div
                                className={`industries-panel-item fintech-item ${hoveredIndustry === 'fintech' ? 'hovered' : ''} ${clickedIndustry === 'fintech' ? 'expanded' : ''}`}
                                data-industry="fintech"
                                onMouseEnter={() => {
                                    setHoveredIndustry('fintech');
                                    // Trigger node hover
                                    const fintechNode = document.querySelector('.industries-fintech-node');
                                    if (fintechNode) {
                                        fintechNode.classList.add('hovered');
                                    }
                                }}
                                onMouseLeave={() => {
                                    setHoveredIndustry(null);
                                    // Remove node hover
                                    const fintechNode = document.querySelector('.industries-fintech-node');
                                    if (fintechNode) {
                                        fintechNode.classList.remove('hovered');
                                    }
                                }}
                                onClick={() => setClickedIndustry(clickedIndustry === 'fintech' ? null : 'fintech')}
                            >
                                <div className="industries-panel-item-header">
                                    <div className="industries-panel-icon">
                                        <DollarSign />
                                    </div>
                                    <div className="industries-panel-name">FinTech</div>
                                </div>
                                {clickedIndustry === 'fintech' && (
                                    <div className="industries-panel-content">
                                        Payment gateway, AML, risk management, multi-channel integration
                                    </div>
                                )}
                            </div>
                            <div
                                className={`industries-panel-item martech-item ${hoveredIndustry === 'martech' ? 'hovered' : ''} ${clickedIndustry === 'martech' ? 'expanded' : ''}`}
                                data-industry="martech"
                                onMouseEnter={() => {
                                    setHoveredIndustry('martech');
                                    const martechNode = document.querySelector('.industries-martech-node');
                                    if (martechNode) martechNode.classList.add('hovered');
                                }}
                                onMouseLeave={() => {
                                    setHoveredIndustry(null);
                                    const martechNode = document.querySelector('.industries-martech-node');
                                    if (martechNode) martechNode.classList.remove('hovered');
                                }}
                                onClick={() => setClickedIndustry(clickedIndustry === 'martech' ? null : 'martech')}
                            >
                                <div className="industries-panel-item-header">
                                    <div className="industries-panel-icon">
                                        <BarChart3 />
                                    </div>
                                    <div className="industries-panel-name">MarTech</div>
                                </div>
                                {clickedIndustry === 'martech' && (
                                    <div className="industries-panel-content">
                                        Lead generation, governance, qualification & delivery
                                    </div>
                                )}
                            </div>
                            <div
                                className={`industries-panel-item edtech-item ${hoveredIndustry === 'edtech' ? 'hovered' : ''} ${clickedIndustry === 'edtech' ? 'expanded' : ''}`}
                                data-industry="edtech"
                                onMouseEnter={() => {
                                    setHoveredIndustry('edtech');
                                    const edtechNode = document.querySelector('.industries-edtech-node');
                                    if (edtechNode) edtechNode.classList.add('hovered');
                                }}
                                onMouseLeave={() => {
                                    setHoveredIndustry(null);
                                    const edtechNode = document.querySelector('.industries-edtech-node');
                                    if (edtechNode) edtechNode.classList.remove('hovered');
                                }}
                                onClick={() => setClickedIndustry(clickedIndustry === 'edtech' ? null : 'edtech')}
                            >
                                <div className="industries-panel-item-header">
                                    <div className="industries-panel-icon">
                                        <GraduationCap />
                                    </div>
                                    <div className="industries-panel-name">EdTech</div>
                                </div>
                                {clickedIndustry === 'edtech' && (
                                    <div className="industries-panel-content">
                                        IoT enabled learning, positive behavioural reinforcement, performance tracking
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Side Panel - Industry Names */}
                    <div className="industries-side-panel industries-right-panel">
                        <div className="industries-panel-list">
                            <div
                                className={`industries-panel-item healthtech-item ${hoveredIndustry === 'healthtech' ? 'hovered' : ''} ${clickedIndustry === 'healthtech' ? 'expanded' : ''}`}
                                data-industry="healthtech"
                                onMouseEnter={() => {
                                    setHoveredIndustry('healthtech');
                                    const healthtechNode = document.querySelector('.industries-healthtech-node');
                                    if (healthtechNode) healthtechNode.classList.add('hovered');
                                }}
                                onMouseLeave={() => {
                                    setHoveredIndustry(null);
                                    const healthtechNode = document.querySelector('.industries-healthtech-node');
                                    if (healthtechNode) healthtechNode.classList.remove('hovered');
                                }}
                                onClick={() => setClickedIndustry(clickedIndustry === 'healthtech' ? null : 'healthtech')}
                            >
                                <div className="industries-panel-item-header">
                                    <div className="industries-panel-icon">
                                        <HeartPulse />
                                    </div>
                                    <div className="industries-panel-name">HealthTech</div>
                                </div>
                                {clickedIndustry === 'healthtech' && (
                                    <div className="industries-panel-content">
                                        EHR/EMR, RCM, patient portals, ABDM, FHIR, HIPAA, OCR, HL7, Document management
                                    </div>
                                )}
                            </div>
                            <div
                                className={`industries-panel-item ecommerce-item ${hoveredIndustry === 'ecommerce' ? 'hovered' : ''} ${clickedIndustry === 'ecommerce' ? 'expanded' : ''}`}
                                data-industry="ecommerce"
                                onMouseEnter={() => {
                                    setHoveredIndustry('ecommerce');
                                    const ecommerceNode = document.querySelector('.industries-ecommerce-node');
                                    if (ecommerceNode) ecommerceNode.classList.add('hovered');
                                }}
                                onMouseLeave={() => {
                                    setHoveredIndustry(null);
                                    const ecommerceNode = document.querySelector('.industries-ecommerce-node');
                                    if (ecommerceNode) ecommerceNode.classList.remove('hovered');
                                }}
                                onClick={() => setClickedIndustry(clickedIndustry === 'ecommerce' ? null : 'ecommerce')}
                            >
                                <div className="industries-panel-item-header">
                                    <div className="industries-panel-icon">
                                        <ShoppingBag />
                                    </div>
                                    <div className="industries-panel-name">eCommerce</div>
                                </div>
                                {clickedIndustry === 'ecommerce' && (
                                    <div className="industries-panel-content">
                                        Robust, scalable platforms with payment integrations and advanced analytics
                                    </div>
                                )}
                            </div>
                            <div
                                className={`industries-panel-item building-item ${hoveredIndustry === 'building' ? 'hovered' : ''} ${clickedIndustry === 'building' ? 'expanded' : ''}`}
                                data-industry="building"
                                onMouseEnter={() => {
                                    setHoveredIndustry('building');
                                    const buildingNode = document.querySelector('.industries-building-node');
                                    if (buildingNode) buildingNode.classList.add('hovered');
                                }}
                                onMouseLeave={() => {
                                    setHoveredIndustry(null);
                                    const buildingNode = document.querySelector('.industries-building-node');
                                    if (buildingNode) buildingNode.classList.remove('hovered');
                                }}
                                onClick={() => setClickedIndustry(clickedIndustry === 'building' ? null : 'building')}
                            >
                                <div className="industries-panel-item-header">
                                    <div className="industries-panel-icon">
                                        <Building />
                                    </div>
                                    <div className="industries-panel-name">Smart Building</div>
                                </div>
                                {clickedIndustry === 'building' && (
                                    <div className="industries-panel-content">
                                        Fire systems, CCTV, BMS, access control, IoT
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="industries-star-network">
                        {/* Industry Nodes */}
                        {industries.map((industry) => {
                            const Icon = industry.icon;
                            return (
                                <div
                                    key={industry.id}
                                    className={`industries-industry-node industries-${industry.id}-node ${industry.position} ${hoveredIndustry === industry.id ? 'hovered' : ''} ${clickedIndustry === industry.id ? 'expanded' : ''}`}
                                    onMouseEnter={() => {
                                        setHoveredIndustry(industry.id);
                                        // Trigger side panel hover
                                        const sidePanelItem = document.querySelector(`[data-industry="${industry.id}"]`);
                                        if (sidePanelItem) {
                                            sidePanelItem.classList.add('hovered');
                                        }
                                    }}
                                    onMouseLeave={() => {
                                        setHoveredIndustry(null);
                                        // Remove side panel hover
                                        const sidePanelItem = document.querySelector(`[data-industry="${industry.id}"]`);
                                        if (sidePanelItem) {
                                            sidePanelItem.classList.remove('hovered');
                                        }
                                    }}
                                    onClick={() => setClickedIndustry(clickedIndustry === industry.id ? null : industry.id)}
                                >
                                    <div className="industries-node-core">
                                        <Icon />
                                    </div>
                                </div>
                            );
                        })}

                        {/* Connection Lines */}
                        <svg className="industries-connection-lines" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
                            {/* Cross connections between industries */}
                            <line className="industries-cross-connection fintech-healthtech" x1="50" y1="20" x2="80" y2="35" />
                            <line className="industries-cross-connection ecommerce-building" x1="80" y1="65" x2="50" y2="80" />
                            <line className="industries-cross-connection ecommerce-martech" x1="80" y1="65" x2="20" y2="35" />
                            <line className="industries-cross-connection building-martech" x1="50" y1="80" x2="20" y2="35" />
                            <line className="industries-cross-connection edtech-fintech" x1="20" y1="65" x2="50" y2="20" />
                            <line className="industries-cross-connection martech-fintech" x1="20" y1="35" x2="50" y2="20" />
                            <line className="industries-cross-connection edtech-building" x1="20" y1="65" x2="50" y2="80" />
                            <line className="industries-cross-connection edtech-healthtech" x1="20" y1="65" x2="80" y2="35" />
                            <line className="industries-cross-connection ecommerce-fintech" x1="80" y1="65" x2="50" y2="20" />
                            <line className="industries-cross-connection healthtech-building" x1="80" y1="35" x2="50" y2="80" />
                        </svg>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Industries;
